import React from 'react';
import { KanbanIcon, ListIcon } from '../../../components/icons';
import { ToggleGroup, ToggleGroupItem } from '../../../components/ui/toggle-group';
import { cn } from '@/lib/utils';

type ViewMode = 'kanban' | 'list';

interface ViewModeSwitchProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  className?: string;
  itemClassName?: string;
  /** Sem borda própria — para faixa unificada no mobile */
  embedded?: boolean;
}

const EMBEDDED_BTN =
  'inline-flex h-11 min-w-0 flex-1 items-center justify-center rounded-none border-0 touch-manipulation transition-colors';

export const ViewModeSwitch: React.FC<ViewModeSwitchProps> = ({
  viewMode,
  onViewModeChange,
  className,
  itemClassName,
  embedded = false,
}) => {
  if (embedded) {
    return (
      <div
        role="group"
        aria-label="Modo de visualização"
        className={cn('flex h-11 min-w-0 items-stretch divide-x divide-border', className)}
      >
        <button
          type="button"
          aria-label="Visualização Kanban"
          aria-pressed={viewMode === 'kanban'}
          title="Visualização Kanban"
          onClick={() => onViewModeChange('kanban')}
          className={cn(
            EMBEDDED_BTN,
            viewMode === 'kanban'
              ? 'bg-primary-subtle text-primary'
              : 'bg-transparent text-secondary hover:bg-surface-subtle hover:text-primary',
            itemClassName,
          )}
        >
          <KanbanIcon className="h-5 w-5" aria-hidden />
        </button>
        <button
          type="button"
          aria-label="Visualização em Lista"
          aria-pressed={viewMode === 'list'}
          title="Visualização em Lista"
          onClick={() => onViewModeChange('list')}
          className={cn(
            EMBEDDED_BTN,
            viewMode === 'list'
              ? 'bg-primary-subtle text-primary'
              : 'bg-transparent text-secondary hover:bg-surface-subtle hover:text-primary',
            itemClassName,
          )}
        >
          <ListIcon className="h-5 w-5" aria-hidden />
        </button>
      </div>
    );
  }

  return (
    <ToggleGroup
      type="single"
      value={viewMode}
      onValueChange={(value) => {
        if (value === 'kanban' || value === 'list') onViewModeChange(value);
      }}
      variant="outline"
      size="default"
      spacing={0}
      className={cn('h-9 rounded-lg border border-border bg-card', className)}
      aria-label="Modo de visualização"
    >
      <ToggleGroupItem
        value="kanban"
        aria-label="Visualização Kanban"
        title="Visualização Kanban"
        className={cn(
          'h-9 min-w-9 rounded-none data-[state=on]:bg-primary-subtle data-[state=on]:text-primary',
          itemClassName,
        )}
      >
        <KanbanIcon className="h-5 w-5" />
      </ToggleGroupItem>
      <ToggleGroupItem
        value="list"
        aria-label="Visualização em Lista"
        title="Visualização em Lista"
        className={cn(
          'h-9 min-w-9 rounded-none data-[state=on]:bg-primary-subtle data-[state=on]:text-primary',
          itemClassName,
        )}
      >
        <ListIcon className="h-5 w-5" />
      </ToggleGroupItem>
    </ToggleGroup>
  );
};

export default ViewModeSwitch;
